import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../../config/config';
import { Button, ButtonSize } from '../../../shared/components/button/button';
import { Radio } from '../../../shared/components/radio/radio';
import { useToast } from '../../../shared/hooks/use-toast';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { getErrorMessage } from '../../../shared/utils/convert-error';
import type { IUserSessionCumulativeLimit } from '../../limits.interface';
import { SessionContextType, UserLimitPeriod } from '../../limits.interface';
import {
    ICreatorName,
    useAddUserSessionLimitCumulativeMutation,
} from '../../queries/session-limit-cumulative.graphql';
import { FormSection } from '../form-section/form-section';

import styles from './session-limit-cumulative-form.module.scss';

const style = createStyleHelper(styles, 'session-limit-cumulative-form');

export const SessionLimitCumulativeForm: FunctionComponent = () => {
    const intl = useIntl();
    const auth = useAuth();
    const toast = useToast();
    const profileId = auth.user?.profile?.user_profile_id as string;

    const {
        register,
        handleSubmit,
        formState: { isDirty, isValid },
        reset,
    } = useForm<IUserSessionCumulativeLimit>({ mode: 'onChange' });

    const [{ fetching }, execute] = useAddUserSessionLimitCumulativeMutation();

    const onSubmit = useCallback(
        async (limitData: IUserSessionCumulativeLimit) => {
            const { error } = await execute({
                userProfileId: profileId,
                xTenantID: config.TENANT_ID,
                creatorName: ICreatorName.User,
                limitInfo: {
                    createdBy: profileId,
                    sessionContextType: SessionContextType.LOGIN,
                    duration: limitData.duration,
                    period: limitData.period,
                },
            });

            if (error) {
                toast.error(getErrorMessage(intl, 'user.limits.create', error));
                return;
            }

            toast.success(
                intl.formatMessage({
                    id: 'user.limit.created.success',
                    defaultMessage: 'User Limit Created',
                }),
            );
            reset();
        },
        [execute, profileId, toast, intl, reset],
    );

    return (
        <form className={style()} onSubmit={handleSubmit(onSubmit)}>
            <FormSection
                title="limits.session-limit-cumulative-form.limit.title"
                description="session-limit-cumulative-form.limit.description"
            >
                <Radio
                    {...register('duration', {
                        required: true,
                    })}
                    name="duration"
                    value={UserLimitPeriod.PT2H}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="session-limit-form.time.two-hours.name"
                            defaultMessage={'2 Hours'}
                        />
                    }
                />
                <Radio
                    {...register('duration', {
                        required: true,
                    })}
                    name="duration"
                    value={UserLimitPeriod.PT5H}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="session-limit-form.time.five-hours.name"
                            defaultMessage={'5 Hours'}
                        />
                    }
                />
                <Radio
                    {...register('duration', {
                        required: true,
                    })}
                    name="duration"
                    value={UserLimitPeriod.PT10H}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="session-limit-form.time.ten-hours.name"
                            defaultMessage={'10 Hours'}
                        />
                    }
                />
                <Radio
                    {...register('duration', {
                        required: true,
                    })}
                    name="duration"
                    value={UserLimitPeriod.PT15H}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="session-limit-form.time.fifthteen-hours.name"
                            defaultMessage={'15 Hours'}
                        />
                    }
                />
            </FormSection>

            <FormSection
                title="limits.session-limit-cumulative-form.period.title"
                description="session-limit-cumulative-form.period.description"
            >
                <Radio
                    {...register('period', {
                        required: true,
                    })}
                    name="period"
                    value={UserLimitPeriod.P1D}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="session-limit-form.time.one-day.name"
                            defaultMessage={'24 Hours'}
                        />
                    }
                />
                <Radio
                    {...register('period', {
                        required: true,
                    })}
                    name="period"
                    value={UserLimitPeriod.P7D}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="session-limit-form.time.seven-days.name"
                            defaultMessage={'7 Days'}
                        />
                    }
                />
                <Radio
                    {...register('period', {
                        required: true,
                    })}
                    name="period"
                    value={UserLimitPeriod.P30D}
                    className={style('option')}
                    label={
                        <FormattedMessage
                            id="session-limit-form.period.one-month.name"
                            defaultMessage={'30 Days'}
                        />
                    }
                />
            </FormSection>

            <Button
                size={ButtonSize.LARGE}
                type="submit"
                loading={fetching}
                disabled={!isDirty || !isValid}
            >
                <FormattedMessage id="session-limit-form.submit" defaultMessage="Save Changes" />
            </Button>
        </form>
    );
};
